import {
  LinearProgressProps,
  Theme,
  Typography,
  useTheme,
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { FC } from 'react';
import { useSnapshot } from 'valtio';
import ProgressWithInfo from '../../elements/ProgressWithInfo/ProgressWithInfo';
import ActionScanButton from '../components/ActionScanButton';
import DiscoveryStore from '../store/discovery-store';
import GradualStore from '../store/gradual-store';
import ScanStore, { ScanPhase } from '../store/scan-store';
import {
  dismissScanError,
  openCancelScanModal,
} from '../store/scan-store-actions';
import CancelScanContainer from './CancelScanContainer';

const ScannerProgressContainer: FC<
  React.PropsWithChildren<LinearProgressProps>
> = props => {
  const { phase, errorMessage } = useSnapshot(ScanStore);
  const { classes } = useStyles();
  const {
    value: discoveryValue,
    total: discoveryTotal,
    computedValue: discoveryComputedValue,
    info: discoveryInfo,
  } = useSnapshot(DiscoveryStore);
  const theme = useTheme();
  const { info: gradualInfo } = useSnapshot(GradualStore);

  if (phase === ScanPhase.Gradual) {
    return (
      <div className={classes.container}>
        <CancelScanContainer />
        <ProgressWithInfo
          value={gradualInfo.value}
          info={gradualInfo.text}
          onCancel={openCancelScanModal}
          {...props}
        />
        <ActionScanButton value='Cancel Scan' onClick={openCancelScanModal} />
      </div>
    );
  }

  if (phase === ScanPhase.ScanStarted) {
    return (
      <div className={classes.container}>
        <ProgressWithInfo
          value={discoveryComputedValue}
          info={discoveryInfo}
          total={discoveryTotal}
          onCancel={openCancelScanModal}
          formatTotal={''}
          {...props}
        />
      </div>
    );
  }

  if (phase === ScanPhase.Discovery) {
    return (
      <div className={classes.container}>
        <CancelScanContainer />
        <ProgressWithInfo
          value={discoveryComputedValue}
          info={discoveryInfo}
          total={discoveryTotal}
          onCancel={openCancelScanModal}
          formatTotal={
            discoveryTotal
              ? `${discoveryValue} out of ${discoveryTotal} applications were discovered`
              : ''
          }
          {...props}
        />
        <ActionScanButton value='Cancel Scan' onClick={openCancelScanModal} />
      </div>
    );
  }

  if (phase === ScanPhase.Error) {
    return (
      <div className={classes.container}>
        <Typography variant='body1' color={theme.palette.progress.textColor}>
          {errorMessage}
        </Typography>
        <ActionScanButton onClick={dismissScanError} value='Dismiss' />
      </div>
    );
  }

  return null;
};

const useStyles = makeStyles()((theme: Theme) => ({
  container: {
    minHeight: 50,
    gap: theme.spacing(1),
    paddingInline: theme.spacing(1.5),
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    backgroundColor: theme.palette.progress.backgroundColor,
  },
}));

export default ScannerProgressContainer;
